import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useTable} from 'react-table';
import * as actions from '../../actions/TasksTableActions';
import * as columnFormatters from '../../../_common/column-formatters';
import * as customColumnFormatters from '../column-formatters';
import {useTasksUIContext} from '../TasksUIContext';
import useInfiniteScrollControl from '../../../_common/components/_custom-hooks/useInfiniteScrollControl';
import CommonGrid from '../../../_common/components/CommonGrid';
import CommonGridSpinner from '../../../_common/components/CommonGridSpinner';
import SortingColumnHeader from "../../../_common/components/SortingColumnHeader/SortingColumnHeader";

export default function TasksTable() {
  const {formatMessage} = useIntl();
  const [tableRows, setTableRows] = useState([]);
  const prefix = 'tasks-page-column';
  const columns = useMemo(() => [
    {
      accessor: 'taskID',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-taskID`, defaultMessage: 'Task ID'})}
          fieldName={'taskID'}
          useContext={useTasksUIContext}
          showDefaultIcon={true}
        />
    },
    {
      accessor: 'username',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-responsibleUser`, defaultMessage: 'Responsible user'})}
          fieldName={'username'}
          useContext={useTasksUIContext}
        />
    },
    {
      accessor: 'firmShumaIDAndPropertyType',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-firmShumaIDAndPropertyType`, defaultMessage: 'Firm shuma ID + property type'})}
          fieldName={'firmShumaID'}
          useContext={useTasksUIContext}
        />
    },
    {
      accessor: 'taskTypeAndTaskStatus',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-taskTypeAndTaskStatus`, defaultMessage: 'Task type + task status'})}
          fieldName={'taskType'}
          useContext={useTasksUIContext}
        />
    },
    {
      accessor: 'customers',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-customers`, defaultMessage: 'Customers'})}
          fieldName={'customers'}
          useContext={useTasksUIContext}
        />,
    },
    {
      accessor: 'fullAddress',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-fullAddress`, defaultMessage: 'Full address'})}
          fieldName={'fullAddress'}
          useContext={useTasksUIContext}
        />,
    },
    {
      accessor: 'dueDate',
      Header:
        <SortingColumnHeader
          text={formatMessage({id: `${prefix}-dueDate`, defaultMessage: 'Due date'})}
          fieldName={'dueDate'}
          useContext={useTasksUIContext}
        />
    },
    {
      accessor: 'actions',
      Header: '',
      width: 90
    },
  ], []);
  const data = useMemo(() => [
    ...tableRows.map((item) => {
      return {
        taskID: columnFormatters.BoldColumnFormatter(item.taskID, 'xl'),
        username: columnFormatters.BoldColumnFormatter(item.username),
        firmShumaIDAndPropertyType: columnFormatters.SeveralItemsColumnFormatter([item.firmShumaID, item.propertyTypeName]),
        taskTypeAndTaskStatus: customColumnFormatters.TasksTypeAndStatusColumnFormatter(item),
        customers: columnFormatters.SeveralItemsColumnFormatter(item.customers),
        fullAddress: columnFormatters.AddressColumnFormatter(item.cityName, item.streetName, item.houseNumber),
        dueDate: columnFormatters.DateColumnFormatter(item.dueDate),
        actions: customColumnFormatters.TasksActionsColumnFormatter(item),
      };
    })
  ], [tableRows]);

  const infiniteScrollProps = useInfiniteScrollControl({
    currentStateStoreId: 'tasksTableReducer',
    UIContext: useTasksUIContext(),
    getNewEntities: actions.getTasks,
    getNextEntities: actions.getNextTasks,
    onEntitiesChange: entities => {
      entities
        ? setTableRows(entities)
        : setTableRows([]);
    },
    startRequestedPageNumber: 0,
    dataLength: tableRows.length,
    scrollableTarget: 'scrollContainer',
    scrollThreshold: 0.9,
    loader: (<CommonGridSpinner/>),
    style: {
      overflow: 'inherit'
    },
  });

  return (
    <CommonGrid
      infiniteScrollProps={infiniteScrollProps}
      {...useTable({columns, data})}
    />
  );
}
